/**
 * src/components/marketing/PromoCountdownBanner.jsx
 *
 * Banner sticky con countdown para la promo LANZAMIENTO25 (25% OFF
 * primer mes + 7 días de trial gratis). Activa el sesgo de escasez +
 * cierra la objeción "ya lo veré después".
 *
 * Lógica:
 * - Cookie `dr_victor_promo_countdown_start` guarda el timestamp de la
 *   primera visita del user (PRIMERA vez que ve el banner).
 * - El countdown corre 7 días desde ese momento, NO desde la creación del
 *   cupón. Así cada user tiene su propio reloj personalizado → urgencia
 *   real y consistente sin importar cuándo entra al funnel.
 * - Después de los 7 días: el banner NO aparece. La cookie sigue en
 *   localStorage para no re-empezar el countdown si vuelve.
 * - Si user clickea cerrar (X), guardamos `dr_victor_promo_dismissed=1` en
 *   localStorage y no vuelve a aparecer (sesión completa).
 *
 * Visual: sticky top, fondo navy con borde dorado, texto blanco. Countdown
 * en formato "Xd Yh" (o "Yh Zm" si <24h). Botón "Copiar código" que copia
 * LANZAMIENTO25 al clipboard con feedback visual de 2s.
 *
 * Solo se muestra si `useMarketingConsent()` devuelve true (respeto
 * de cookies de marketing). NO porque el countdown sea de marketing
 * (es UX), sino porque el banner promociona conversiones — sin consent
 * preferimos no mostrarlo.
 *
 * Por qué se monta en /planesbeta y /planes:
 * - /planesbeta es el destino de las campañas Meta C3 (carrusel orgánico)
 * - /planes es la página pública estándar
 * - Ambas son páginas de alta intención donde el banner ayuda a convertir
 */

'use client';

import { useEffect, useState, useRef } from 'react';
import { useMarketingConsent } from '@/lib/cookie-consent';

const PROMO_CODE = 'LANZAMIENTO25';
const COUNTDOWN_DURATION_MS = 7 * 24 * 60 * 60 * 1000; // 7 días
const STORAGE_KEY_START = 'dr_victor_promo_countdown_start';
const STORAGE_KEY_DISMISSED = 'dr_victor_promo_dismissed';
// Re-render del countdown cada minuto (no por segundo — sería ruido visual).
const REFRESH_INTERVAL_MS = 60 * 1000;

export default function PromoCountdownBanner() {
  const consentGranted = useMarketingConsent();
  const [remainingMs, setRemainingMs] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const [copyFeedback, setCopyFeedback] = useState(false);
  // Ref para guardar el interval id y limpiarlo en cleanup.
  const intervalRef = useRef(null);

  // Init: leer/crear el timestamp de inicio, evaluar si todavía está dentro
  // del countdown. Solo corre en client (SSR-safe gracias a useEffect).
  useEffect(() => {
    if (typeof window === 'undefined') return;

    // Si el user dismissó el banner antes, no lo volvemos a mostrar.
    if (window.localStorage.getItem(STORAGE_KEY_DISMISSED) === '1') {
      setDismissed(true);
      return;
    }

    let startMs = parseInt(window.localStorage.getItem(STORAGE_KEY_START) || '0', 10);
    if (!startMs || isNaN(startMs)) {
      startMs = Date.now();
      try { window.localStorage.setItem(STORAGE_KEY_START, String(startMs)); }
      catch { /* private mode — banner sigue funcionando pero sin persistencia */ }
    }

    const updateRemaining = () => {
      const elapsed = Date.now() - startMs;
      const left = COUNTDOWN_DURATION_MS - elapsed;
      setRemainingMs(left > 0 ? left : 0);
    };

    updateRemaining();
    intervalRef.current = setInterval(updateRemaining, REFRESH_INTERVAL_MS);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  // Gates: no consent → no banner. Dismissed → no banner. Expirado → no banner.
  if (!consentGranted) return null;
  if (dismissed) return null;
  if (remainingMs === null) return null; // todavía no hidrató del localStorage
  if (remainingMs <= 0) return null;     // pasaron los 7 días

  // Formato compacto: si quedan >=24h muestra "Xd Yh"; si <24h muestra "Yh Zm".
  const days = Math.floor(remainingMs / (24 * 60 * 60 * 1000));
  const hours = Math.floor((remainingMs % (24 * 60 * 60 * 1000)) / (60 * 60 * 1000));
  const minutes = Math.floor((remainingMs % (60 * 60 * 1000)) / (60 * 1000));
  const timeText = days >= 1
    ? `${days}d ${hours}h`
    : `${hours}h ${minutes}m`;

  // Urgencia visual progresiva: cuando quedan <24h, color de countdown
  // pasa a rojo suave para reforzar el sesgo de scarcity sin parecer spam.
  const isLastDay = remainingMs < 24 * 60 * 60 * 1000;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(PROMO_CODE);
      setCopyFeedback(true);
      setTimeout(() => setCopyFeedback(false), 2000);
    } catch {
      // Fallback: si clipboard API no está disponible (HTTP, iframe, etc.)
      // seleccionamos el texto en un input invisible. Best-effort.
      const ta = document.createElement('textarea');
      ta.value = PROMO_CODE;
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); setCopyFeedback(true); setTimeout(() => setCopyFeedback(false), 2000); }
      catch { /* swallow */ }
      document.body.removeChild(ta);
    }
  };

  const handleDismiss = () => {
    setDismissed(true);
    try { window.localStorage.setItem(STORAGE_KEY_DISMISSED, '1'); }
    catch { /* swallow */ }
  };

  return (
    <div
      role="region"
      aria-label="Promoción de lanzamiento"
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 100,
        width: '100%',
        background: 'linear-gradient(90deg, #0F172A 0%, #1E293B 50%, #0F172A 100%)',
        borderBottom: '1px solid #D4AF37',
        color: '#FFFFFF',
        padding: '10px 16px',
        fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
        fontSize: '14px',
        boxShadow: '0 1px 4px rgba(0,0,0,0.3)',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '12px',
          flexWrap: 'wrap',
          maxWidth: '1200px',
          margin: '0 auto',
        }}
      >
        <span style={{ fontSize: '16px' }} aria-hidden="true">🎁</span>

        <span style={{ fontWeight: 500 }}>
          Tarifa de lanzamiento — <strong style={{ color: '#D4AF37' }}>25% OFF</strong> tu primer mes +{' '}
          <strong style={{ color: '#D4AF37' }}>3 días gratis</strong> sin tarjeta
        </span>

        <button
          type="button"
          onClick={handleCopy}
          aria-label={`Copiar código promocional ${PROMO_CODE}`}
          style={{
            background: copyFeedback ? '#10B981' : '#D4AF37',
            color: '#0F172A',
            border: 'none',
            padding: '6px 12px',
            borderRadius: '6px',
            fontWeight: 700,
            fontFamily: 'monospace',
            fontSize: '13px',
            cursor: 'pointer',
            letterSpacing: '0.5px',
            transition: 'background 0.2s ease',
          }}
        >
          {copyFeedback ? '✓ Copiado' : `Copiar ${PROMO_CODE}`}
        </button>

        <span
          style={{
            color: isLastDay ? '#F87171' : '#FCD34D',
            fontWeight: 600,
            fontVariantNumeric: 'tabular-nums',
            // 'tabular-nums' evita que cambien de ancho los números al refrescar
          }}
        >
          Termina en {timeText}
        </span>

        <button
          type="button"
          onClick={handleDismiss}
          aria-label="Cerrar banner"
          style={{
            background: 'transparent',
            color: 'rgba(255,255,255,0.6)',
            border: 'none',
            cursor: 'pointer',
            fontSize: '18px',
            padding: '0 4px',
            lineHeight: 1,
            marginLeft: '4px',
          }}
        >
          ×
        </button>
      </div>
    </div>
  );
}
